import Node from 'nanogl-node'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import ShoppingEntity from '@/webgl/runner/entities/ShoppingEntity'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import FrozenEffect from '@/webgl/runner/rendering/effects/FrozenEffect'
import TextRenderer from '@/webgl/runner/rendering/TextRenderer'
import RunnerCamera from '@/webgl/runner/RunnerCamera'
import ScenarioAudio from '@/webgl/runner/scenarios/ScenarioAudio'
import { AudioSegment } from '@/webgl/runner/scenarios/segments/AudioSegment'
import Countdown from '@/webgl/runner/scenarios/segments/Countdown'
import { Intro } from '@/webgl/runner/scenarios/segments/Intro'
import { Outro } from '@/webgl/runner/scenarios/segments/Outro'
import { PlaySegment } from '@/webgl/runner/scenarios/segments/PlaySegment'
import { Popin } from '@/webgl/runner/scenarios/segments/Popin'
import {
  ShoppingBalanceSegment,
  ShoppingBalanceTransition,
  ShoppingCartSegment,
} from '@/webgl/runner/scenarios/segments/ShoppingSegments'
import { TransitionSegment } from '@/webgl/runner/scenarios/segments/TransitionSegment'
import RunnerRenderer from '@/webgl/runner/SceneRenderer'
import PostprocessSystem from '@/webgl/runner/systems/PostprocessSystem'
import SequencingSystem from '@/webgl/runner/systems/SequencingSystem'

import Tooltip from '../segments/Tooltip'
import SETUP from './Francesca2Setup'

export default class Francesca2Scene implements IScene {
  glRenderer: Renderer
  root: Node
  camCtrl: RunnerCamera
  world: GameWorld
  renderer: IRunnerRenderer
  text: TextRenderer
  frozen: FrozenEffect
  audio: ScenarioAudio

  constructor(renderer: Renderer) {
    this.glRenderer = renderer

    this.root = new Node()
    this.world = new GameWorld(this.root)
    this.camCtrl = new RunnerCamera(this.world)
    this.renderer = new RunnerRenderer(renderer, this.world, SETUP)

    this.world.getTrackingSystem().useDomElement(this.glRenderer.ilayer)

    this.world.registry.init(SETUP)

    const postprocess = new PostprocessSystem(this.world, this.glRenderer.postprocess)
    postprocess.enableFrozenFeature()

    this.world.systems.add(postprocess)

    this.world.shopping = new ShoppingEntity()

    this.audio = new ScenarioAudio('en', SETUP.audio, SETUP.audioSfx)

    this.world.background.spawnableItems = SETUP.sequences[0].scrolling

    const sequencing = new SequencingSystem(this.world)
    this.world.systems.add(sequencing)

    sequencing
      .init(this.renderer, SETUP)
      .add(new Intro('introduction'))
      .add(new AudioSegment('audio_intro'))
      .add(new Popin('popin1', true))
      .add(new AudioSegment('audio_shop'))
      .add(new Countdown('countdown'))
      .add(new Tooltip('move'))
      .add(new Tooltip('pause'))
      .add(new ShoppingCartSegment('shopping'))
      .add(new ShoppingCartSegment('shopping_bis'))
      .add(new PlaySegment('receipt'))
      .add(new TransitionSegment('shopping_end'))
      .add(new ShoppingBalanceTransition('audio_receipt'))
      .add(new AudioSegment('audio_balance'))
      .add(new Popin('popin2'))
      .add(new ShoppingBalanceSegment('shopping_balance'))
      .add(new AudioSegment('audio_outro'))
      // .add(new DelaySegment("shopping_end_delay", 2.5))
      .add(new Outro('outro'))
    // .add(new TransitionSegment("outro"))

    /// #if _DEBUG
    const params = new URLSearchParams(window.location.search)
    if (params.get('segment')) {
      sequencing.setStartSegmentId(params.get('segment'))
    }
    /// #endif
  }

  public setActor(actor: RunnerActor) {
    this.world.state.setActor(actor)
    this.world.shopping.setActor(actor)
    this.world.shopping.sync()
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([
      // this.frozen.load(),
      this.renderer.load(abort),
      this.world.systems.get<SequencingSystem>(SequencingSystem).load(abort),
      this.audio.load(abort),
    ])
  }

  start() {
    this.glRenderer.cameras.getManager('main').setControler(this.camCtrl)

    this.world.state.setJauge(0)
    this.world.start()
  }

  preRender(): void {
    this.world.update(this.glRenderer.dt)
    const time = this.world.time
    this.renderer.preRender(time)
    this.root.updateWorldMatrix()
  }

  rttPass(context: RenderContext): void {
    this.renderer.rttPass(context)
  }

  render(context: RenderContext): void {
    this.renderer.render(context)
    this.world.systems.forEach((system) => system.render?.(context))

    if (this.camCtrl.camera) DebugDraw.drawFrustum(this.camCtrl.camera._viewProj)
  }

  dispose(): void {
    this.world?.systems.get<PostprocessSystem>(PostprocessSystem)?.disableFrozenFeature()
    this.world?.dispose()
    this.audio?.dispose()
  }
}
